// Confirm and correct the dec page values, and compare the coverages (spec C10), in the Record's
// words: the field names, how a value shows and how it is typed, what AMS360 has for it, and the
// coverage rows prior vs renewal. Pure functions, so the panel, the dialog and the tests agree.
import type { Coverage, Reading, ReadingField, ReadingFixField, RenewalDetail } from '../auth/api';
import { longDate, money } from './renewal-lines';

/** The dialog title and the row name of each value. */
export const fixLabels: Record<ReadingFixField, string> = {
  totalPremium: 'Renewal premium',
  policyNumber: 'Policy #',
  namedInsured: 'Named insured',
  effectiveDate: 'Term starts',
  expirationDate: 'Term ends',
};

/** One of the values the VA can confirm or correct. */
export const isFixField = (s: string): s is ReadingFixField => Object.hasOwn(fixLabels, s);

/** The rows under the premium, in this order (the premium has its own place at the top). */
export const fixRows: ReadingFixField[] = [
  'policyNumber',
  'namedInsured',
  'effectiveDate',
  'expirationDate',
];

const kindOf = (f: ReadingFixField) =>
  f === 'totalPremium'
    ? 'money'
    : f === 'effectiveDate' || f === 'expirationDate'
      ? 'date'
      : 'text';

/** Money with cents only when there are any: "$2,505" or "$2,505.50". */
const moneyCents = (n: number) =>
  Number.isInteger(n)
    ? money(n)
    : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** A value as the Record shows it: "$2,505", "Tue, Nov 10, 2026", "48 10168-01". */
export function showValue(field: ReadingFixField, v: string | number | null): string {
  if (v === null || v === '') return '';
  if (kindOf(field) === 'money') return moneyCents(Number(v));
  if (kindOf(field) === 'date') return longDate(String(v));
  return String(v);
}

const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const shortDate = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/** A value in the input, as it is typed: "2,505", "Nov 10, 2026". */
export function inputValue(field: ReadingFixField, v: string | number | null): string {
  if (v === null || v === '') return '';
  if (kindOf(field) === 'money') {
    const n = Number(v);
    return n.toLocaleString('en-US', {
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  }
  if (kindOf(field) === 'date') return shortDate.format(new Date(`${v}T00:00:00Z`));
  return String(v);
}

function ymd(y: number, m: number, d: number): string | null {
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) {
    return null;
  }
  return y < 2000 || y > 2100 ? null : date.toISOString().slice(0, 10);
}

/** "Nov 30, 2026", "November 30 2026", "11/30/2026" or "2026-11-30" as 2026-11-30. */
export function parseDate(raw: string): string | null {
  const t = raw.trim().toLowerCase();
  let m = /^([a-z]{3})[a-z]*\.?\s+(\d{1,2}),?\s+(\d{4})$/.exec(t);
  if (m) {
    const month = months.indexOf(m[1]) + 1;
    return month ? ymd(Number(m[3]), month, Number(m[2])) : null;
  }
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
  if (m) return ymd(Number(m[3]), Number(m[1]), Number(m[2]));
  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  return m ? ymd(Number(m[1]), Number(m[2]), Number(m[3])) : null;
}

/**
 * What the VA typed, in the form the server keeps (2026-11-30, 2505.00), or the reason it is not
 * a value of that field. An empty input is neither.
 */
export function parseInput(
  field: ReadingFixField,
  raw: string,
): { value: string } | { error: string } | null {
  const t = raw.replace(/\s+/g, ' ').trim();
  if (!t) return null;
  switch (kindOf(field)) {
    case 'money': {
      const ok = /^\$?\s*(\d{1,3}(,\d{3})*|\d+)(\.\d{1,2})?$/.test(t);
      const n = ok ? Number(t.replace(/[$,\s]/g, '')) : NaN;
      if (!ok || !Number.isFinite(n)) return { error: 'Enter an amount like 1,996.' };
      if (n > 10_000_000) return { error: 'Enter an amount under $10,000,000.' };
      return { value: n.toFixed(2) };
    }
    case 'date': {
      const d = parseDate(t);
      return d ? { value: d } : { error: 'Enter a date like Nov 30, 2026.' };
    }
    default:
      if (field === 'policyNumber') {
        return /^[A-Za-z0-9][A-Za-z0-9 ./-]{0,39}$/.test(t)
          ? { value: t }
          : { error: 'Use letters, digits, spaces, dots, dashes or slashes, at most 40.' };
      }
      return t.length <= 150
        ? { value: t }
        : { error: 'Enter the name as printed, at most 150 characters.' };
  }
}

/** The same value in the form the server keeps, to tell a confirm from a correction. */
export function keptForm(field: ReadingFixField, v: string | number | null): string | null {
  if (v === null || v === '') return null;
  return kindOf(field) === 'money' ? Number(v).toFixed(2) : String(v).replace(/\s+/g, ' ').trim();
}

/** What AMS360 has for the value, for the dialog's "Prior, from AMS360". */
export function priorOf(field: ReadingFixField, r: RenewalDetail): string {
  switch (field) {
    case 'totalPremium':
      return r.priorPremium === null ? 'Not in AMS360' : moneyCents(r.priorPremium);
    case 'policyNumber':
      return r.policyNumber;
    case 'namedInsured':
      return r.insuredFull;
    case 'effectiveDate':
      return `${longDate(r.expirationDate)} (current term ends)`;
    case 'expirationDate':
      return r.policy.priorEffectiveDate
        ? `Current term ${longDate(r.policy.priorEffectiveDate)} to ${longDate(r.expirationDate)}`
        : `Current term ends ${longDate(r.expirationDate)}`;
  }
}

const stamp = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'America/New_York',
});

/** "Nov 12 14:20", as the Record board writes it. */
export function fixStamp(at: string): string {
  const p = Object.fromEntries(stamp.formatToParts(new Date(at)).map((x) => [x.type, x.value]));
  return `${p.month} ${p.day} ${p.hour}:${p.minute}`;
}

/** The tooltip on an Edited tag: "Read as 1,000. Changed by Nora Bell, Nov 12 14:20." */
export function editedTip(f: ReadingField): string {
  const who = `${f.fix?.by ?? 'someone'}, ${f.fix ? fixStamp(f.fix.at) : ''}`;
  const read = f.read === null ? 'Not read from the PDF' : `Read as ${showValue(f.field, f.read)}`;
  return `${read}. Changed by ${who}.`;
}

/** The line in the dialog about the last confirm or correction. */
export function fixLine(f: ReadingField): string | null {
  if (!f.fix) return null;
  const who = `${f.fix.by ?? 'someone'}, ${fixStamp(f.fix.at)}`;
  return f.fix.action === 'corrected'
    ? `Corrected to ${showValue(f.field, f.value)} by ${who}.`
    : `Confirmed by ${who}.`;
}

/** "2 fields need review: Policy #, Term starts" */
export function reviewLine(fields: ReadingField[]): string {
  const names = fields.map((f) => fixLabels[f.field]).join(', ');
  return fields.length === 1
    ? `1 field needs review: ${names}`
    : `${fields.length} fields need review: ${names}`;
}

/** The fields still to check, in the panel's order (the premium first). */
export const fieldsToCheck = (reading: Reading): ReadingField[] =>
  (['totalPremium', ...fixRows] as ReadingFixField[])
    .map((k) => reading.fields.find((f) => f.field === k))
    .filter((f): f is ReadingField => !!f && f.check !== null);

/** The toast after a save: "Renewal premium corrected." */
export const fixToast = (field: ReadingFixField, action: 'confirm' | 'correct') =>
  `${fixLabels[field]} ${action === 'confirm' ? 'confirmed' : 'corrected'}.`;

/** The toast after its Undo. */
export const fixUndoneText = 'Undone. The value is back as it was.';
export const fixUndoFailedText = 'Could not undo. The value stays.';

// ---------- coverages, prior vs renewal ----------

export type CoverageRow = {
  name: string;
  renewal: string | null;
  prior: string | null;
  /** The limit or the deductible differs, or the row is on one side only. */
  changed: boolean;
};

const key = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
const same = (a: string, b: string) => key(a) === key(b);

/**
 * A limit or deductible as a value: each number as a number ("$1,000.00" is "$1,000"), with the $,
 * the % and the words kept, so "1.5%" and "15%" or "$1,000.00" and "$100,000" stay apart.
 */
const valueKey = (s: string) =>
  s
    .toLowerCase()
    .replace(/\d[\d,]*(\.\d+)?/g, (n) => String(Number(n.replace(/,/g, ''))))
    .replace(/\s+/g, '');
const sameValue = (a: string, b: string) => valueKey(a) === valueKey(b);

/** "$300,000" or "$300,000, deductible $1,000" */
export function coverageText(c: Coverage): string {
  const limit = c.limit.trim();
  const ded = c.deductible.trim();
  if (limit && ded) return `${limit}, deductible ${ded}`;
  if (ded) return `Deductible ${ded}`;
  return limit || 'Included';
}

/**
 * The coverage rows of the renewal dec page next to the prior term's, matched by name. With no
 * prior reading, every row is just the renewal's and none is marked changed.
 */
export function coverageRows(renewal: Coverage[], prior: Coverage[] | null): CoverageRow[] {
  const rows: CoverageRow[] = [];
  const used = new Set<number>();
  for (const c of renewal) {
    if (!prior) {
      rows.push({ name: c.name, renewal: coverageText(c), prior: null, changed: false });
      continue;
    }
    const i = prior.findIndex((p, n) => !used.has(n) && same(p.name, c.name));
    const p = i >= 0 ? prior[i] : null;
    if (p) used.add(i);
    rows.push({
      name: c.name,
      renewal: coverageText(c),
      prior: p ? coverageText(p) : null,
      changed: !p || !sameValue(p.limit, c.limit) || !sameValue(p.deductible, c.deductible),
    });
  }
  prior?.forEach((p, n) => {
    if (!used.has(n))
      rows.push({ name: p.name, renewal: null, prior: coverageText(p), changed: true });
  });
  return rows;
}
